/**
 * @file index.tsx
 *
 * @fileoverview Renders a set of cards representing video teasers.
 */
import React, { useContext } from 'react';
import {
  VideoTeaserItemParagraph,
  VideoTeasersParagraph,
} from '~/jobiqo/types/types';
import styles from './videoTeaserRow.module.scss';
import EmbeddedVideo from '~/jobiqo/components/Landingpage/EmbeddedYoutube/components/EmbeddedVideo';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { FormattedMessage } from 'react-intl';
import CMPServiceCheckbox from '~/jobiqo/components/CMPserviceCheckbox';
import { extractServiceFromUrl } from '~/jobiqo/utils/links';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Grid, GridColumnsProp } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Hr } from '~/vcl/atoms/06-layout/10-hr';
import { AlignedCenter } from '~/vcl/atoms/06-layout/01-alignment/01-align-center';
import {
  checkVideoConsent,
  mapVideoTypeToService,
} from '~/jobiqo/components/Landingpage/utils/landingPage';

interface VideoTeaserCardsProps {
  row: VideoTeasersParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const VideoTeaserCards = ({
  row,
  maxContainerSize,
}: VideoTeaserCardsProps) => {
  const userContext: UserContextInterface = useContext(UserContext);

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={12}
      paddingBottom={12}
      backgroundColor="primaryLight"
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        {row.teasersRowTitle && (
          <>
            <Text textAlign={'center'} fontSize={5}>
              {row.teasersRowTitle}
            </Text>
            <Spacing size="small" />
          </>
        )}
        {(row as any).items && (row as any).items.length > 0 && (
          <Grid
            columns={[1, 1, (row as any).items.length as GridColumnsProp]}
            gap={7}
          >
            {(row as any).items.map(
              (item: VideoTeaserItemParagraph, index: number) => {
                let consented = true;
                let service = '';
                if (item.link.url) {
                  service = extractServiceFromUrl(item.link.url);
                  consented = checkVideoConsent(service, userContext);
                }

                return (
                  <Cell display="grid" key={index}>
                    <Box className={styles.cardBox}>
                      <FlexItems>
                        {item && (
                          <FlexItem flexGrow={5}>
                            <FlexItems
                              flexDirection={'row'}
                              className={styles.header}
                            >
                              {item.logo && (
                                <img
                                  className={styles.logo}
                                  loading="lazy"
                                  src={item.logo.url}
                                  alt={item.logo.alt ?? ''}
                                />
                              )}
                              {item.teaserTitle && (
                                <Text fontSize={3} marginLeft={5}>
                                  {item.teaserTitle}
                                </Text>
                              )}
                            </FlexItems>
                            <Spacing size="medium">
                              <Hr />
                            </Spacing>
                            {item.link.url && consented ? (
                              <Cell gridColumn={'span 3'}>
                                <FlexItems justifyContent="center">
                                  <EmbeddedVideo link={item.link} />
                                </FlexItems>
                              </Cell>
                            ) : (
                              <AlignedCenter>
                                <Text marginTop="0">
                                  <FormattedMessage
                                    id="video_teasers_row_consent"
                                    defaultMessage="Consent to this service"
                                  />
                                </Text>
                                <Spacing />
                                <CMPServiceCheckbox
                                  serviceId={mapVideoTypeToService(service)}
                                />
                              </AlignedCenter>
                            )}
                            {item.subtitle && (
                              <Text fontSize={2}>{item.subtitle}</Text>
                            )}
                          </FlexItem>
                        )}
                      </FlexItems>
                    </Box>
                  </Cell>
                );
              },
            )}
          </Grid>
        )}
      </Main>
    </Box>
  );
};
export default VideoTeaserCards;
